import React, {useEffect} from 'react'
import {useSelector, useDispatch, shallowEqual} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {FiLogOut} from 'react-icons/fi'
import Item from './Item'
import Logo from './Logo'
import useIsDesktop from './useIsDesktop'
import '../../css/components/left-sidebar-1.css'
import {apiLogout} from "../../pages/LoginPage/Actions";
import Toast from "cogo-toast";
import {USER_RESET} from "../../reducers/user";

const SidebarNew = () => {
    const {navigation, userState, collapsedFlag, dark} = useSelector(
        (state) => ({
            navigation: state.navigation,
            userState: state.user,
            collapsedFlag: !!state.config?.collapsed,
            dark: state.palettes?.background === 'dark'
        }),
        shallowEqual
    )
    const dispatch = useDispatch()
    const location = useLocation()
    const isDesktop = useIsDesktop()
    let navigate = useNavigate();

    const logout = () => {
        apiLogout((data) => {
            Toast.success("Byl jste úspěšně odhlášen.")
            dispatch({type: USER_RESET})
            localStorage.removeItem('authorization-leg-enigoo')
            navigate('/login')
        })
    }
    // Na mobilu je collapsed=true "menu otevřené" (off-canvas výsuv) — ikonový
    // režim (schované texty, hover flyouty) proto platí jen na desktopu.
    const collapsed = collapsedFlag && isDesktop

    // Mobil: po přechodu na jinou stránku automaticky zavřít výsuvné menu.
    useEffect(() => {
        if (!isDesktop && collapsedFlag) {
            dispatch({type: 'SET_CONFIG_KEY', key: 'collapsed', value: false})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    const userName = userState?.user?.name
    const activeArealName = userState?.activeAreal?.name

    const getInitials = (name) => {
        if (!name) return 'U'
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    return (
        <aside
            className="left-sidebar left-sidebar-1 h-screen sticky top-0 bg-white border-r border-grey-200 flex flex-col justify-between flex-shrink-0 z-40 text-grey-900 font-sans">
            {/* 1. Horní část: Logo */}
            <div className="pt-6 flex-shrink-0">
                <Logo/>
            </div>

            {/* 2. Prostřední část: Navigace (Jediná skrolovatelná oblast) */}
            <div className={`flex-1 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'} space-y-4 my-2 min-h-0`}>
                {navigation?.map((menu, i) => (
                    <div key={i} className="space-y-1">
                        {menu.title && !collapsed && (
                            <div
                                style={{color: dark ? '#94a3b8' : '#505f76', fontSize: '11px'}}
                                className="px-4 pt-2 pb-1 font-bold uppercase tracking-wider"
                            >
                                <span>{menu.title}</span>
                            </div>
                        )}

                        <ul className="space-y-1">
                            {menu.items?.map((l0, a) => (
                                <li key={a} className={`l0 ${collapsed ? 'relative' : ''}`}>
                                    <Item {...l0} collapsed={collapsed}>
                                        {l0.items && l0.items.length > 0 && (
                                            <ul
                                                style={collapsed ? undefined : {borderColor: 'rgba(195, 198, 215, 0.5)'}}
                                                className={collapsed ? 'space-y-1' : 'ml-4 space-y-1 border-l pl-2'}
                                            >
                                                {l0.items.map((l1, b) => (
                                                    <li key={b} className="l1">
                                                        <Item {...l1}>
                                                            {l1.items && l1.items.length > 0 && (
                                                                <ul className="ml-3 space-y-1">
                                                                    {l1.items.map((l2, c) => (
                                                                        <li key={c} className="l2">
                                                                            <Item {...l2}>
                                                                                {l2.items && l2.items.length > 0 && (
                                                                                    <ul className="ml-3 space-y-1">
                                                                                        {l2.items.map((l3, d) => (
                                                                                            <li key={d} className="l3">
                                                                                                <Item {...l3}>
                                                                                                    {l3.items && l3.items.length > 0 && (
                                                                                                        <ul className="ml-3 space-y-1">
                                                                                                            {l3.items.map((l4, e) => (
                                                                                                                <li key={e}
                                                                                                                    className="l4">
                                                                                                                    <Item {...l4} />
                                                                                                                </li>
                                                                                                            ))}
                                                                                                        </ul>
                                                                                                    )}
                                                                                                </Item>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </Item>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </Item>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </Item>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {/* 3. Spodní část: Profil (Pevně přišpendlený dole) */}
            <div
                style={{borderColor: 'rgba(195, 198, 215, 0.4)'}}
                className={`pt-4  border-t flex-shrink-0 bg-white dark:bg-grey-900 ${
                    collapsed ? 'px-2 pb-4' : 'pb-4 px-4'
                }`}
            >
                <div className={`flex items-center w-full ${collapsed ? 'justify-center' : ''}`}>

                    <div className={`flex items-center overflow-hidden ${collapsed ? '' : 'flex-1 min-w-0 mr-2'}`}>
                        <div
                            style={{backgroundColor: '#004ac6', color: '#ffffff', width: 36, height: 36}}
                            title={collapsed ? userName || 'Uživatel' : undefined}
                            className="rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                        >
                            {getInitials(userName)}
                        </div>

                        {!collapsed && (
                            <div className="ml-3 min-w-0">
                                <p
                                    style={{color: dark ? '#e2e8f0' : '#2d3748'}}
                                    className="text-sm font-semibold truncate leading-tight"
                                >
                                    {userName || 'Uživatel'}
                                </p>
                                <p
                                    style={{color: dark ? '#a0aec0' : '#718096', fontSize: '11px'}}
                                    className="truncate"
                                >
                                    {activeArealName ? `Areál: ${activeArealName}` : 'ENIGOO'}
                                </p>
                            </div>
                        )}
                    </div>

                    {!collapsed && (
                        <button
                            type="button"
                            aria-label="Odhlásit se"
                            data-tooltip="Odhlásit se"
                            className="sidebar-logout text-grey-400 dark:text-grey-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors duration-150 focus:outline-none flex-shrink-0"
                            onClick={logout}
                        >
                            <FiLogOut size={18}/>
                        </button>
                    )}

                </div>

            </div>
        </aside>
    )
}

export default SidebarNew
